import { cn } from '@lensora/ui'
import { Link } from 'react-router-dom'

import { FaviconImg } from './FaviconImg'

import { getSourceTypeBadgeClass, type SourceType } from '@/lib/source-types'

export interface CitedDomain {
  domain: string
  times_cited: number
  unique_prompts: number
  urls: string[]
  type: SourceType
}

interface TopCitedSourcesProps {
  domains: CitedDomain[]
  isLoading: boolean
}

export function TopCitedSources({ domains, isLoading }: TopCitedSourcesProps) {
  const sources = domains.slice(0, 10)

  return (
    <div className="box-border flex h-full flex-col gap-3 overflow-hidden rounded-lg border border-border bg-surface">
      <div className="relative h-[90px] shrink-0 overflow-hidden">
        <img
          src="/Citation_Banner_LM.png"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent" />
        <div className="absolute inset-0 z-10 flex items-center justify-between px-5">
          <div>
            <p className="mb-0.5 text-sm font-bold text-text-inverse [text-shadow:0_1px_4px_rgba(0,0,0,0.6)]">
              Top cited sources
            </p>
            <p className="text-[11px] text-text-inverse/80 [text-shadow:0_1px_4px_rgba(0,0,0,0.6)]">
              Most cited pages by AI models
            </p>
          </div>
          <Link
            to="/dashboard/sources"
            className="rounded-full border border-white/25 bg-black/35 px-2.5 py-1 text-[11px] text-text-inverse hover:bg-black/50"
          >
            View all →
          </Link>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 pb-5">
        {isLoading ? (
          <div className="flex flex-col gap-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-10 animate-pulse rounded-lg bg-surface-overlay" />
            ))}
          </div>
        ) : sources.length === 0 ? (
          <p className="text-xs text-text-muted">
            No citation data yet — run prompts on Perplexity, Google AI, ChatGPT Search, or Claude Search to start tracking sources.
          </p>
        ) : (
          <div className="flex flex-col">
            {sources.map((source, i) => (
              <div
                key={source.domain}
                className={cn(
                  'flex items-center gap-3 py-2.5',
                  i < sources.length - 1 && 'border-b border-border'
                )}
              >
                <FaviconImg domain={source.domain} name={source.domain} size={20} className="rounded" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold text-text">{source.domain}</p>
                </div>
                <span
                  className={cn(
                    'shrink-0 rounded-full border px-1.5 py-0.5 text-[10px]',
                    getSourceTypeBadgeClass(source.type)
                  )}
                >
                  {source.type}
                </span>
                <span className="min-w-[40px] shrink-0 text-right text-xs font-semibold text-text-muted">
                  {source.times_cited}×
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
